import Select from '@r1-frontend/ui-react/components/select/select';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import pick from 'lodash/pick';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import NoSSR from '~/src/helpers/noSSR';
import { selectCompanyName } from '~/src/store/selectors/city';
import * as newsSelectors from '~/src/store/selectors/news';

import { PaginationLinks } from '../PaginationLinks';
import { CardNews } from './card-news';
import { selectTitleByTag } from './helpers';
import * as ST from './styled';
import { TagsLinks } from './tags-links';

const getYearsOptions = () => {
    const currentYear = Number(new Date().getFullYear());
    const firstNewsYear = 2012;
    const years = new Array(currentYear - firstNewsYear + 1)
        .fill(null)
        .map((_, index) =>
            ({
                value: `${firstNewsYear + index}`,
                label: `${firstNewsYear + index}`,
            })).reverse();
    return [{ value: '', label: 'Год' }, ...years];
};

const MONTH_OPTIONS = [
    { value: '', label: 'Месяц' },
    { value: '1', label: 'Январь' },
    { value: '2', label: 'Февраль' },
    { value: '3', label: 'Март' },
    { value: '4', label: 'Апрель' },
    { value: '5', label: 'Май' },
    { value: '6', label: 'Июнь' },
    { value: '7', label: 'Июль' },
    { value: '8', label: 'Август' },
    { value: '9', label: 'Сентябрь' },
    { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' },
    { value: '12', label: 'Декабрь' },
];

class News extends PureComponent {
    static propTypes = {
        news: PropTypes.array,
        router: PropTypes.object.isRequired,
        tagsItems: PropTypes.array,
        currentPage: PropTypes.number.isRequired,
        isFetching: PropTypes.bool.isRequired,
        pageCount: PropTypes.number.isRequired,
    }

    static defaultProps = {
        news: [],
        tagsItems: [],
    }

    constructor(props) {
        super(props);
        this.yearsOptions = getYearsOptions();
    }

    getOptions = (options, value) => {
        return options.find(item => item.value === value);
    }

    getPageOptions = (pageCount) => {
        if (!pageCount) {
            return [];
        }

        return Array(pageCount).fill(null).map((_, i) => {
            const val = i + 1;
            return (
                {
                    id: val,
                    label: `${val}`,
                    value: `${val}`,
                }
            );
        });
    }

    getDisplayPath(path) {
        const index = path.search(/\?/g);
        return path.substring(0, index < 0 ?  path.length : index);
    }

    onChangeYear = (selected) => {
        this.onChangeFilter(selected, 'year');
    }

    onChangeMonth = (selected) => {
        this.onChangeFilter(selected, 'month');
    }

    onChangeFilter = (selected, type) => {
        const { router, isFetching } = this.props;

        if (isFetching) {
            return;
        }

        const { asPath, pathname, query } = router;

        let value;
        if (isArray(selected)) {
            value = selected.map(item => item.value);
            if (value.length === 0) {
                value = '';
            }
        } else {
            value = get(selected, 'value', '');
        }

        const newQuery = pick(query, ['year', 'month']);

        newQuery.page = '1';

        if (value) {
            newQuery[type] = value;
        } else {
            delete newQuery[type];
        }

        const newAsPath = this.getDisplayPath(asPath);

        router.push({
            pathname,
            query: newQuery,
        }, {
            pathname: newAsPath,
            query: newQuery,
        });
    }

    renderNewsList() {
        const { news } = this.props;

        const sortedNews = news.sort((a, b) => {
            return b.pinned - a.pinned;
        });

        return sortedNews.map((item, index) => {
            const isFirstItem = index === 0;
            return (
                <ST.ListItem key={item.id} isFirstItem={isFirstItem} data-test='news-list-item'>
                    <CardNews item={item} isFirstItem={isFirstItem} />
                </ST.ListItem>
            );
        });
    }

    render() {
        const { router, currentPage, pageCount, isFetching, tagsItems, tagAlias, companyName } = this.props;
        const { pathname, asPath, query } = router;
        const { tags, month, year } = query;
        const pagesOptions = this.getPageOptions(pageCount);
        const currentMonth = this.getOptions(MONTH_OPTIONS, month);
        const currentYear = this.getOptions(this.yearsOptions, year);

        const displayPath = this.getDisplayPath(asPath);

        return (
            <ST.Content>
                <ST.InnerWrap>
                    <ST.Title>{selectTitleByTag({ tagAlias, companyName })}</ST.Title>

                    <NoSSR>
                        <ST.DateFilters>
                            <Select
                                options={this.yearsOptions}
                                value={currentYear || null}
                                onChange={this.onChangeYear}
                                placeholder='Год'
                                width='160px'
                            />
                            <Select
                                options={MONTH_OPTIONS}
                                value={currentYear ? currentMonth : null}
                                onChange={this.onChangeMonth}
                                placeholder='Месяц'
                                width='160px'
                                isDisabled={!currentYear}
                            />
                        </ST.DateFilters>
                    </NoSSR>

                    <ST.TagsWrap>
                        {tagsItems &&
                            <TagsLinks
                                options={tagsItems}
                                query={pick(query, ['year', 'month'])}
                                selectedAlias={tags}
                            />
                        }
                    </ST.TagsWrap>

                    <ST.List data-test='news-list'>
                        {isFetching && <ST.Preloader />}
                        {!isFetching && this.renderNewsList()}
                    </ST.List>

                    <ST.PaginationWrap>
                        <PaginationLinks
                            options={pagesOptions}
                            selectedId={currentPage}
                            countBtns={3}
                            href={pathname}
                            as={displayPath}
                            query={pick(query, ['year', 'month'])}
                        />
                    </ST.PaginationWrap>
                </ST.InnerWrap>
            </ST.Content>
        );
    }
}

const mapStateToProps = state => ({
    news: newsSelectors.selectAndSortNews(state),
    isFetching: get(state, 'news.fetching'),
    pageCount: get(state, 'news.pageCount'),
    currentPage: get(state, 'news.currentPage'),
    tagsItems: get(state, 'tags.items'),
    companyName: selectCompanyName(state),
});


export default withRouter(connect(mapStateToProps)(News));
